import { Outlet, useLocation } from 'react-router'
import { clearPaintedColorTheme, syncColorTheme } from '@/lib/color-theme'
import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import { AuthBanner } from './AuthBanner'
import { DemoBanner } from './DemoBanner'
import { ClinicProfileBanner } from './ClinicProfileBanner'
import { LapsedStrip } from './LapsedStrip'
import { PastDueStrip } from './PastDueStrip'
import { TrialNotesSpentStrip } from './TrialNotesSpentStrip'
import { PendingUploadBanner } from './PendingUploadBanner'
import { RecordingErrorBanner } from './RecordingErrorBanner'
import { RecordingSilencePrompt } from './RecordingSilencePrompt'
import { RunOnRecordingBanner } from './RunOnRecordingBanner'
import { IosBrowserRecordingNotice } from './IosBrowserRecordingNotice'
import { GetTheAppNotice } from './GetTheAppNotice'
import { PractitionerRoleBanner } from './PractitionerRoleBanner'
import { EmailVerificationBanner, EmailVerificationGate } from './EmailVerificationBanner'
import { useVocabularyHydration } from '@/api/vocabulary-hooks'
import { PlatformStatusBanner } from './PlatformStatusBanner'
import { Sidebar } from './Sidebar'
import { CommandPalette } from './CommandPalette'
import { HelpQuickView } from './HelpPanel'
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp'
import { FeedbackModal, useFeedbackCommands } from '@/features/feedback'
import { RecordingFAB } from '../notes/RecordingFAB'
import { ScribePopoutProvider } from '../notes/ScribePopoutHost'
const CallPanel = lazy(() => import('../call/CallPanel').then(m => ({ default: m.CallPanel })))
import { useAuthStore } from '@/stores/auth'
import { useWsStore } from '@/stores/websocket'
import { useRecordingStore } from '@/stores/recording'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useCallKeyboardShortcuts } from '@/hooks/useCallKeyboardShortcuts'
import { commandRegistry } from '@/utils/commandRegistry'
import { useNavigate } from 'react-router'
import {
  FileText, Users, Calendar, CalendarCheck, Settings, MessageSquare, Building2,
  LayoutDashboard, UserPlus, Pill, Stethoscope, Shield, Tag, Wrench,
  BarChart3, AlertTriangle, DatabaseZap, Upload, Menu,
  ShieldCheck, FileWarning,
  Megaphone, CircleHelp,
} from 'lucide-react'
import { ToastContainer } from '@/components/shared/ToastContainer'
import { UpgradePanel } from '@/components/shared/UpgradePanel'
import { retryOrphanedRecordings } from '@/lib/call-recording-db'
import { api } from '@/api/client'
import { readPrefilledData } from '@/components/onboarding/prefilled'
import { needsPractitionerIdentity } from '@/lib/identity-gate'
import { AgreementGate } from '@/components/auth/AgreementGate'
import { PasskeyOfferGate } from '@/components/auth/PasskeyOfferGate'
import { UploadRetryScheduler } from '@/lib/upload-retry-scheduler'
import { getPendingRecordings } from '@/lib/note-recording-db'
import { SETTINGS_PAGES } from '@/components/settings/settingsPages'
import ClaroLogo from '@/components/shared/ClaroLogo'
import { useAftercareSheetsCommand } from '@/components/manage/clinic/aftercareSheetsCommand'
import { regionalText } from '@/lib/regional-copy'

/**
 * App shell — persistent layout that wraps all authenticated routes.
 *
 * Provides sidebar navigation, WebSocket connection, recording state,
 * and video call overlay. Components inside the Outlet can navigate
 * freely without losing any of this state.
 */
export function AppShell() {
  const { init, isLoading, isRetrying, isAuthenticated } = useAuthStore()
  // CLA-1148 — adopt the served practitioner-type vocabulary once per session.
  // Mounted here rather than in each picker because the same list labels other
  // users' roles on admin and manage screens, synchronously, mid-render.
  useVocabularyHydration()
  const { connect, disconnect } = useWsStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false)
  const location = useLocation()

  const navigate = useNavigate()
  const showShortcutsHelp = useCallback(() => setShortcutsHelpOpen(true), [])
  useKeyboardShortcuts({ onShowHelp: showShortcutsHelp })
  useCallKeyboardShortcuts()
  useFeedbackCommands()
  useAftercareSheetsCommand()

  // Register base navigation commands in the command palette
  useEffect(() => {
    return commandRegistry.registerMany([
      { id: 'nav-notes', label: 'Notes', icon: FileText, path: '/', keywords: ['notes', 'home', 'recordings'], shortcut: 'g n', category: 'navigation', showInDefault: true, action: () => navigate('/') },
      { id: 'nav-patients', label: 'Patients', icon: Users, path: '/patients', keywords: ['patients', 'people'], shortcut: 'g p', category: 'navigation', showInDefault: true, action: () => navigate('/patients') },
      { id: 'nav-calendar', label: 'Calendar', icon: Calendar, path: '/calendar', keywords: ['calendar', 'schedule', 'appointments', 'booking'], shortcut: 'g c', category: 'navigation', showInDefault: true, action: () => navigate('/calendar') },
      { id: 'nav-confirmations', label: 'Confirmations', subtitle: 'Bookings waiting on you', icon: CalendarCheck, path: '/confirmations', keywords: ['confirmations', 'pending', 'booking', 'consult', 'accept', 'decline'], shortcut: 'g f', category: 'navigation', showInDefault: true, action: () => navigate('/confirmations') },
      { id: 'nav-bookings-calendar', label: 'My day', subtitle: 'Your bookings — pending + confirmed', icon: Calendar, path: '/bookings/calendar', keywords: ['my day', 'bookings', 'schedule', 'today'], category: 'navigation', showInDefault: false, action: () => navigate('/bookings/calendar') },
      { id: 'nav-messages', label: 'Messages', icon: MessageSquare, path: '/messaging', keywords: ['messages', 'messaging', 'chat'], shortcut: 'g m', category: 'navigation', showInDefault: true, action: () => navigate('/messaging') },
      { id: 'nav-inbox', label: 'Inbox', icon: DatabaseZap, path: '/inbox', keywords: ['inbox', 'documents', 'referrals'], shortcut: 'g i', category: 'navigation', showInDefault: true, action: () => navigate('/inbox') },
      { id: 'nav-order', label: 'Order', icon: Building2, path: '/order', permission: 'settings:manage', keywords: ['order', 'store', 'shop', 'buy', 'wholesale'], shortcut: 'g o', category: 'navigation', showInDefault: true, action: () => navigate('/order') },
      { id: 'nav-safety', label: 'Safety', subtitle: 'Incident register + MD inbox', icon: ShieldCheck, path: '/safety', keywords: ['safety', 'compliance', 'incident', 'incidents', 'register', 'md inbox', 'auditor'], category: 'navigation', showInDefault: true, permission: 'incidents:read', action: () => navigate('/safety') },
      { id: 'action-report-incident', label: 'Report an incident', subtitle: 'Safety & Compliance', icon: FileWarning, path: '/safety/incidents/new', keywords: ['report', 'incident', 'adverse', 'near miss', 'safety', 'complaint', 'error'], category: 'action', showInDefault: true, permission: 'incidents:write', priority: 10, action: () => navigate('/safety/incidents/new') },
      { id: 'nav-help', label: 'Help', subtitle: 'Guides and answers', icon: CircleHelp, path: '/help', keywords: ['help', 'guide', 'guides', 'how to', 'support', 'articles'], category: 'navigation', action: () => navigate(`/help?from=${encodeURIComponent(window.location.pathname)}`) },
      { id: 'nav-settings', label: 'Settings', icon: Settings, path: '/settings', keywords: ['settings', 'preferences'], shortcut: 'g s', category: 'navigation', showInDefault: true, action: () => navigate('/settings') },
      { id: 'nav-manage', label: 'Manage', subtitle: regionalText('Clinic & org'), icon: Building2, path: '/manage', permission: 'settings:manage', keywords: ['manage', 'clinic', 'practice', 'staff', 'organisation', 'organization'], category: 'navigation', showInDefault: true, action: () => navigate('/manage') },
      { id: 'nav-platform', label: 'Platform', subtitle: 'Overview', icon: LayoutDashboard, path: '/manage', permission: 'admin:*', keywords: ['admin', 'platform', 'dashboard'], category: 'navigation', action: () => navigate('/manage') },
      { id: 'nav-platform-users', label: 'Users', subtitle: 'Platform', icon: UserPlus, path: '/manage/platform/users', permission: 'admin:*', keywords: ['users', 'accounts'], category: 'navigation', action: () => navigate('/manage/platform/users') },
      { id: 'nav-platform-status', label: 'Status banner', subtitle: 'Platform', icon: Megaphone, path: '/manage/platform/status', permission: 'admin:*', keywords: ['status','banner','incident','outage','known issue'], category: 'navigation', action: () => navigate('/manage/platform/status') },
      { id: 'nav-platform-treatments', label: 'Treatments', subtitle: 'Platform', icon: Pill, path: '/manage/platform/treatments', permission: 'admin:*', keywords: ['treatments'], category: 'navigation', action: () => navigate('/manage/platform/treatments') },
      { id: 'nav-platform-types', label: 'Consultation Types', subtitle: 'Platform', icon: Stethoscope, path: '/manage/platform/types', permission: 'admin:*', keywords: ['consultation', 'types'], category: 'navigation', action: () => navigate('/manage/platform/types') },
      { id: 'nav-platform-specialties', label: 'Specialties', subtitle: 'Platform', icon: Shield, path: '/manage/platform/specialties', permission: 'admin:*', keywords: ['specialties', 'medical'], category: 'navigation', action: () => navigate('/manage/platform/specialties') },
      { id: 'nav-platform-labels', label: regionalText('Label Catalogue'), subtitle: 'Platform', icon: Tag, path: '/manage/platform/labels', permission: 'admin:*', keywords: ['labels', 'tags'], category: 'navigation', action: () => navigate('/manage/platform/labels') },
      { id: 'nav-platform-consent', label: 'Consent Forms', subtitle: 'Platform', icon: Wrench, path: '/manage/platform/consent-forms', permission: 'admin:*', keywords: ['consent', 'forms'], category: 'navigation', action: () => navigate('/manage/platform/consent-forms') },
      { id: 'nav-platform-prompts', label: 'Prompt Editor', subtitle: 'Platform', icon: MessageSquare, path: '/manage/platform/prompts', permission: 'admin:*', keywords: ['prompts', 'ai', 'system prompt'], category: 'navigation', action: () => navigate('/manage/platform/prompts') },
      { id: 'nav-platform-tokens', label: 'Token Usage', subtitle: 'Platform', icon: BarChart3, path: '/manage/platform/tokens', permission: 'admin:*', keywords: ['tokens', 'usage', 'cost'], category: 'navigation', action: () => navigate('/manage/platform/tokens') },
      { id: 'nav-platform-evaluations', label: 'Model Evaluations', subtitle: 'Platform', icon: BarChart3, path: '/manage/platform/evaluations', permission: 'admin:*', keywords: ['evaluations', 'eval', 'benchmarks', 'models', 'gemini', 'claude'], category: 'navigation', action: () => navigate('/manage/platform/evaluations') },
      { id: 'nav-platform-comparisons', label: 'Shadow Comparisons', subtitle: 'Platform', icon: BarChart3, path: '/manage/platform/comparisons', permission: 'admin:*', keywords: ['shadow', 'comparisons', 'similarity', 'deepgram', 'assemblyai', 'providers'], category: 'navigation', action: () => navigate('/manage/platform/comparisons') },
      { id: 'nav-platform-duplicates', label: 'Patient Duplicates', subtitle: 'Platform', icon: DatabaseZap, path: '/manage/platform/data/duplicates', permission: 'admin:*', keywords: ['duplicates', 'merge'], category: 'navigation', action: () => navigate('/manage/platform/data/duplicates') },
      { id: 'nav-platform-import', label: 'Patient Import', subtitle: 'Platform', icon: Upload, path: '/manage/platform/data/import', permission: 'admin:*', keywords: ['import', 'csv'], category: 'navigation', action: () => navigate('/manage/platform/data/import') },
      { id: 'nav-platform-settings', label: 'Platform Settings', subtitle: 'Platform', icon: Wrench, path: '/manage/platform/settings', permission: 'admin:*', keywords: ['platform', 'defaults'], category: 'navigation', action: () => navigate('/manage/platform/settings') },
      { id: 'nav-platform-compliance', label: 'Compliance Adapters', subtitle: 'Platform', icon: ShieldCheck, path: '/manage/platform/compliance', permission: 'admin:*', keywords: ['compliance', 'adapter', 'tga', 'daen', 'regulator', 'sponsor'], category: 'navigation', action: () => navigate('/manage/platform/compliance') },
      { id: 'nav-platform-danger', label: 'Danger Zone', subtitle: 'Platform', icon: AlertTriangle, path: '/manage/platform/danger', permission: 'admin:*', keywords: ['danger', 'reset'], category: 'navigation', action: () => navigate('/manage/platform/danger') },
      // CLA-1796: every Settings page has an address, so ⌘K can go straight
      // to it. Labs is staff-only, like its menu item and route.
      ...SETTINGS_PAGES.map((page) => {
        const path = `/settings/${page.path}`
        return {
          id: `nav-settings-${page.id}`,
          label: page.label,
          subtitle: 'Settings',
          icon: page.icon,
          path,
          permission: page.staffOnly ? 'admin:*' : undefined,
          keywords: ['settings', ...page.keywords],
          category: 'navigation' as const,
          action: () => navigate(path),
        }
      }),
    ])
  }, [navigate])

  // Close mobile menu on navigation.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- react to router state change.
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Load user context on mount
  useEffect(() => {
    init()
  }, [init])

  // CLA-1894: the user's colour theme belongs to the signed-in app only.
  // Paint it on entering the shell; leaving for a public or patient page
  // (booking, the portal, consent) puts the brand back.
  useEffect(() => {
    syncColorTheme()
    return () => clearPaintedColorTheme()
  }, [])

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      connect()
      return () => {
        disconnect()
      }
    }
  }, [isAuthenticated, connect, disconnect])

  // Standalone owner onboarding (CLA-520) — when an authenticated user has
  // zero clinics, route them into the wizard. Skip the redirect when the
  // user is already on an /onboarding path so the wizard's internal
  // navigation isn't fought.
  const user = useAuthStore((s) => s.user)
  const clinics = useAuthStore((s) => s.clinics)
  const needsOwnerOnboardingFlag = useAuthStore((s) => s.needsOwnerOnboarding)
  const activeClinicId = useAuthStore((s) => s.activeClinicId)
  const inOnboarding = location.pathname.startsWith('/onboarding/')
  // First-run wizard URLs — already-onboarded users hitting these would
  // create duplicate orgs/clinics or accidentally overwrite their
  // practitioner identity. /onboarding/another-clinic is intentionally
  // *not* in this set: it's the canonical re-entry path (sidebar's
  // "Add clinic" goes there) for adding additional clinics post-onboarding.
  const isFirstRunWizardPath =
    location.pathname === '/onboarding/welcome' ||
    location.pathname === '/onboarding/practitioner-identity' ||
    location.pathname === '/onboarding/passkey' ||
    location.pathname === '/onboarding/clinic' ||
    location.pathname === '/onboarding/documents'

  // Identity-only routing (join invites): a member of an EXISTING clinic
  // whose practitioner identity was never captured goes through JUST the
  // PractitionerIdentity step — never FirstClinic, their clinic exists.
  const needsIdentityOnly = needsPractitionerIdentity(user, clinics)

  // CLA-1183 — route the first-run OWNER wizard on the server's explicit
  // answer, never on `clinics.length === 0`. Those are different questions: a
  // network- or org-only member (a franchisor CEO, a group practice manager)
  // holds no clinic_users row and is emphatically not a new owner. Gating on
  // the list length walked Evolution Laser's CEO through provisioning himself
  // an org, a clinic, a practitioner role and a trial subscription in prod.
  //
  // `?? clinics.length === 0` keeps an older backend behaving exactly as
  // before, so a deploy where the SPA lands ahead of the API is a no-op rather
  // than a regression.
  const needsOwnerOnboarding = needsOwnerOnboardingFlag ?? clinics.length === 0

  useEffect(() => {
    if (isAuthenticated && needsOwnerOnboarding && !inOnboarding) {
      navigate('/onboarding/welcome', { replace: true })
    } else if (isAuthenticated && !needsOwnerOnboarding && !inOnboarding) {
      if (needsIdentityOnly) {
        // Join-invited practitioner: clinic membership exists (materialised
        // at invite accept), but no credentials yet. Route straight to the
        // identity step; it lands them back at "/" on completion.
        navigate('/onboarding/practitioner-identity', { replace: true })
        return
      }
      // Just-invited owners with prefilled clinics already have clinic rows
      // (provisioned at magic-link consume — see Onboarding.ensure_clinic_for_user/2
      // in claro-elixir, CLA-658). Without this branch, the standard `clinics.length === 0`
      // gate skips the wizard entirely for them, so they never see the
      // "Your clinics are ready" confirmation step. Route them in.
      const prefilled = readPrefilledData()
      if (Array.isArray(prefilled?.clinics) && prefilled.clinics.length > 0) {
        navigate('/onboarding/welcome', { replace: true })
      }
    } else if (isAuthenticated && !needsOwnerOnboarding && isFirstRunWizardPath) {
      // Inverse: an onboarded user landed on the first-run wizard path.
      // Send them home; they can use sidebar "Add clinic" if they want
      // /onboarding/another-clinic. Exceptions: (a) they just signed in via
      // an invite with prefilled clinics — the FirstClinic step renders the
      // confirmation view rather than the capture form (CLA-658); (b) a
      // join-invited practitioner still owes their identity — the
      // PractitionerIdentity step is exactly where they belong.
      const prefilled = readPrefilledData()
      const hasPrefilledClinics =
        Array.isArray(prefilled?.clinics) && prefilled.clinics.length > 0
      if (!hasPrefilledClinics && !needsIdentityOnly) {
        navigate('/', { replace: true })
      }
    }
  }, [isAuthenticated, needsOwnerOnboarding, inOnboarding, isFirstRunWizardPath, needsIdentityOnly, navigate])

  // Per-clinic accent tint (CLA-520) — drive `--clinic-accent` from the
  // active clinic's `clinic_branding.primary_color`. Falls back to Claro's
  // primary (the brand tokens) when the clinic has no branding row. Set on
  // documentElement so any descendant can pick it up via
  // `bg-[var(--clinic-accent)]` etc.
  useEffect(() => {
    const active = clinics.find((c) => c.id === activeClinicId)
    const colour = active?.primary_color || 'var(--claro-primary)'
    document.documentElement.style.setProperty('--clinic-accent', colour)
  }, [clinics, activeClinicId])

  // Retry any orphaned call recordings left by a previous crash/tab close
  useEffect(() => {
    if (isAuthenticated) {
      retryOrphanedRecordings(api).catch(() => {})
    }
  }, [isAuthenticated])

  // Surface any consult-note audio left on disk by a failed commit, a tab
  // crash, or a laptop lid closing mid-upload (CLA-938). Deliberately a
  // *detect and offer*, not an auto-retry: an unattended retry storm at login
  // is exactly what an API degradation does not need, and the clinician is the
  // one who knows whether the consult is still wanted. Automatic backoff retry
  // is Phase 2. Separate from the call-recording pass above — different queue,
  // different failure modes, different recovery policy.
  useEffect(() => {
    if (isAuthenticated) {
      useRecordingStore.getState().recoverPendingUploads().catch(() => {})
    }
  }, [isAuthenticated])

  // CLA-945 Phase 2 — automatic background retry. Phase 1 deliberately only
  // *offered* recovery, which left web the weakest client: iOS retries
  // unattended for ~100 minutes and Android for ~95 (CLA-963), while here the
  // audio was safe but delivery waited for a human to come back and click.
  //
  // The scheduler carries the original caution rather than discarding it —
  // backoff, one tab at a time, and paused while the tab is hidden.
  useEffect(() => {
    if (!isAuthenticated) return

    const store = useRecordingStore.getState()
    const scheduler = new UploadRetryScheduler({
      list: () => getPendingRecordings(),
      retry: (id) => useRecordingStore.getState().retryUpload(id),
    })

    // A recovered connection is the single best moment to retry, so `online`
    // sweeps immediately instead of waiting out a backoff that was scheduled
    // while there was no network at all.
    const onOnline = () => void scheduler.tick()
    // Don't burn a clinician's mobile data in a background tab; resume — and
    // sweep once — when they come back to it.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduler.start()
        void scheduler.tick()
      } else {
        scheduler.stop()
      }
    }

    if (document.visibilityState === 'visible') scheduler.start()
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibility)
    void store.recoverPendingUploads().catch(() => {})

    return () => {
      scheduler.stop()
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isAuthenticated])

  // F-2 — warn before an accidental refresh/close mid-consult. While recording
  // or paused the audio lives only in memory (IndexedDB persistence happens at
  // stop), so a stray Cmd-R / Cmd-W / tab-close would drop the consult with no
  // way back. `processing` is covered too — the commit is survivable thanks to
  // IndexedDB, but a prompt costs nothing — and `persistenceWarning` is the one
  // case where even a stopped recording has no local backup at all, so it must
  // prompt regardless of state. The handler reads the store at event time, so
  // one lifetime listener suffices rather than re-subscribing on every change.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const { state, persistenceWarning } = useRecordingStore.getState()
      if (state === 'recording' || state === 'paused' || state === 'processing' || persistenceWarning) {
        e.preventDefault()
        // Legacy browsers require returnValue to be set to trigger the prompt;
        // modern browsers ignore the string and show their own generic message.
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="text-center space-y-4 px-4">
          <div className="w-6 h-6 mx-auto border-2 border-claro-200 border-t-claro-600 rounded-full animate-spin" />
          {/*
            A bare spinner cannot say whether it is loading or stuck (CLA-1170).
            The wait here is bounded by the server's Retry-After, which for a
            rate limit can be most of a minute — long enough that silence reads
            as a broken app, and someone force-reloads, which only spends more
            of the budget that caused the wait.
          */}
          {isRetrying && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-ink">Reconnecting…</p>
              <p className="text-xs text-ink-secondary max-w-xs">
                Claro is busy right now. This should clear on its own in a
                moment — you don't need to reload.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-dvh w-full">
        <div className="text-center space-y-4 max-w-sm px-4 mx-auto">
          <p className="text-lg font-medium text-ink">Session ended</p>
          <p className="text-sm text-ink-secondary">
            Your session has expired. Please sign in to continue.
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors"
          >
            Sign in
          </a>
        </div>
      </div>
    )
  }

  // Onboarding wizard renders bare — no sidebar, no banners, no shell chrome.
  // The wizard owns its own header and progress UI. The agreement gate wraps
  // it too — click-through acceptance comes before first use, wizard included.
  // The email gate (CLA-1018) sits OUTSIDE the agreement gate in both
  // branches: past the 7-day grace the agreement endpoints answer 403 too, so
  // accepting terms first would look like a hang. Inside the grace it renders
  // nothing — the strip in the banner stack carries the ask.
  if (inOnboarding) {
    return (
      <EmailVerificationGate>
        <AgreementGate>
          <Outlet />
        </AgreementGate>
      </EmailVerificationGate>
    )
  }

  // CLA-893: the passkey offer sits INSIDE the agreement gate (accepting terms
  // comes first) and outside the shell, so it takes the screen for the one tap
  // it needs. Deliberately not wrapped around the `inOnboarding` branch above —
  // the wizard has its own passkey step and owns that decision.
  return (
    <EmailVerificationGate>
    <AgreementGate>
    <PasskeyOfferGate>
    {/* Owns the floating scribe window. At the shell root so the window
        survives navigation between patients mid-clinic. */}
    <ScribePopoutProvider>
    <div className="flex h-dvh w-full">
      {/* Skip to content — visible only on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[300] focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:bg-claro-600 focus:rounded-lg focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile overlay sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-64 z-50 lg:hidden animate-slide-in">
            <Sidebar mobile onClose={() => setMobileMenuOpen(false)} />
          </div>
        </>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Auth expiry banner — sits above everything when session dies */}
        <AuthBanner />
        {/* Platform-wide known issue (CLA-936). Above the other banners because
            it is the only one that can tell a clinician not to start a consult;
            below AuthBanner because a dead session outranks everything. */}
        <PlatformStatusBanner />
        {/* Demo environment banner — non-dismissable */}
        <DemoBanner />
        {/* "Check your email" (CLA-1018) — a self-serve signup is signed in
            before the link is clicked, so the ask lives here, answerable in
            place with the code. Above the profile nudges: an unproven address
            is the bigger gap, and it has a deadline. */}
        <EmailVerificationBanner />
        {/* "Complete your clinic profile" nudge (CLA-711) — identity-only,
            non-blocking; hidden on /onboarding/* via the bare-render branch. */}
        <ClinicProfileBanner />
        {/* "Set your role" (CLA-1141 follow-up) — below the clinic nudge
            because clinic identity is the bigger gap, and answerable in place
            rather than by navigating. Snoozes for a week on "Not now". */}
        <PractitionerRoleBanner />
        {/* Recording failed to start — mic blocked, busy or not live. First of
            the failure banners because it is the only one that means the
            consult in front of you is NOT being captured right now. */}
        <RecordingErrorBanner />
        {/* A recording has heard nothing for 30 minutes — probably left
            running (CLA-1564). Anywhere in the app, because whoever comes
            back to it may not be on the note. */}
        <RecordingSilencePrompt />
        {/* Another of this clinician's recordings is still live and has gone
            quiet (CLA-1564) — likely left running in the last room. Passive:
            informs, never stops anything. */}
        <RunOnRecordingBanner />
        {/* iPhone in Safari: the mic dies on screen lock — point at the app
            before the first record (CLA-1341). Snoozable. */}
        <IosBrowserRecordingNotice />
        {/* On a computer, never used the app: it signs in by emailed link,
            no password — nothing said so (Dr Chad Carman). Dismiss is final. */}
        <GetTheAppNotice />
        {/* Consult audio saved on this device but not yet submitted (CLA-938) */}
        <PendingUploadBanner />
        {/* The clinic is read-only (CLA-1167). Last of the banners because it
            is a standing state rather than something to act on today, and
            the ones above are all answerable in place. */}
        <LapsedStrip />
        {/* A subscription payment failed (CLA-1574): a dated heads-up, not a
            lock. Renders nothing once LapsedStrip applies. */}
        <PastDueStrip />
        {/* The trial's 30 AI notes are gone but the overnight sweep has not run
            yet, so the clinic is still `trialing` and LapsedStrip cannot know
            (CLA-1364). Mutually exclusive with it by construction. */}
        <TrialNotesSpentStrip />

        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-surface border-b border-border-subtle shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-surface-tertiary text-ink-secondary transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="leading-none"><ClaroLogo size={18} /></h1>
          <div className="w-8" /> {/* Spacer for centering */}
        </header>

        <main id="main-content" className="flex-1 min-h-0 flex flex-col">
          <Outlet />
        </main>
      </div>

      {/* Mobile FAB for recording */}
      <RecordingFAB />

      {/* Video call panel — persists across route changes */}
      <Suspense fallback={null}>
        <CallPanel />
      </Suspense>

      {/* Global command palette (Cmd+K) */}
      <CommandPalette />

      {/* A help article over the current page, from ⌘K's "Read more" (CLA-1838) */}
      <HelpQuickView />

      {/* In-app feedback widget — opens via Cmd+K command, sidebar link, or
          the upcoming error-boundary integration (CLA-619). */}
      <FeedbackModal />

      {/* Keyboard shortcuts help overlay (?) */}
      <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />

      {/* Global toast notifications */}
      <ToastContainer />
      <UpgradePanel />
    </div>
    </ScribePopoutProvider>
    </PasskeyOfferGate>
    </AgreementGate>
    </EmailVerificationGate>
  )
}
